import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { dynamicsEnvironmentApi } from '@/lib/api';
import type { DynamicsEnvironment } from '@/lib/dynamicsEnvironment';
import { cn } from '@/lib/utils';

export const DynamicsEnvironmentToggle: React.FC = () => {
  const queryClient = useQueryClient();
  const [environment, setEnvironment] = React.useState<DynamicsEnvironment>('hml');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const isProduction = environment === 'prd';

  React.useEffect(() => {
    let active = true;
    dynamicsEnvironmentApi
      .get()
      .then((result) => {
        if (active) setEnvironment(result.environment);
      })
      .catch(() => {
        // Sem leitura (rota só de administrador): mostra o padrão, homologação.
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleChange = async (checked: boolean) => {
    const nextEnvironment: DynamicsEnvironment = checked ? 'prd' : 'hml';

    if (nextEnvironment === 'prd') {
      const confirmed = window.confirm(
        'Você está prestes a consultar dados do ERP Dynamics de Produção.\n\n' +
        'A configuração é global: vale para todos os usuários e processos deste ambiente, até você voltar para homologação. ' +
        'Afeta somente consultas ao Dynamics e não altera o envio de pedidos. Deseja continuar?'
      );

      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    try {
      const result = await dynamicsEnvironmentApi.set(nextEnvironment);
      setEnvironment(result.environment);
      void queryClient.invalidateQueries();
      toast.success(result.environment === 'prd' ? 'Consultas ao ERP em Produção.' : 'Consultas ao ERP em Homologação.');
    } catch {
      toast.error('Não foi possível alterar o ambiente das consultas. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between',
        isProduction ? 'border-red-300 bg-red-50/60' : 'border-emerald-200 bg-emerald-50/60'
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn('rounded-md p-2', isProduction ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700')}>
          <Database className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Ambiente das consultas ERP Dynamics (global)</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Altera somente consultas ao Dynamics 365, para todos os usuários e processos deste ambiente. O envio de pedidos ao
            ERP não é afetado. A configuração fica no banco e vale até alterar.
          </p>
        </div>
      </div>
      <div className="flex min-w-fit items-center gap-3 self-end sm:self-auto">
        <span className={cn('text-xs font-bold', !isProduction ? 'text-emerald-700' : 'text-muted-foreground')}>
          HML
        </span>
        <Switch
          checked={isProduction}
          disabled={loading || saving}
          onCheckedChange={handleChange}
          aria-label="Alternar consultas do ERP Dynamics entre homologação e produção"
          className="data-[state=checked]:bg-red-500 data-[state=unchecked]:bg-emerald-500"
        />
        <span className={cn('text-xs font-bold', isProduction ? 'text-red-600' : 'text-muted-foreground')}>
          PRD
        </span>
        <span className={cn('min-w-[148px] rounded-full px-3 py-1 text-center text-xs font-semibold',
          isProduction ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700')}>
          {loading ? 'Carregando...' : isProduction ? 'Consultando Produção' : 'Consultando Homologação'}
        </span>
      </div>
    </div>
  );
};

export default DynamicsEnvironmentToggle;
